import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Mic, Headphones, Radio, Podcast, Users, ArrowRight, PlayCircle, Star } from 'lucide-react';

const PodcastMedia: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Broadcast Media Institute";
    
    return (
        <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
            {/* Top Bar */}
            <div className="bg-slate-900 text-slate-300 py-2 px-6 text-xs font-medium flex justify-between items-center">
                <span>Admissions Open for Fall 2026</span>
                <div className="flex gap-4">
                    <a href="#" className="hover:text-white transition-colors">Alumni</a>
                    <a href="#" className="hover:text-white transition-colors">Portal</a>
                    <a href="#" className="hover:text-white transition-colors">News</a>
                </div>
            </div>

            {/* Navbar */}
            <nav className="sticky top-0 z-50 bg-white border-b border-slate-200">
                <div className="max-w-7xl mx-auto px-6 h-20 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-orange-600 rounded-lg flex items-center justify-center text-white shadow-md">
                            <Mic size={20} />
                        </div>
                        <span className="font-bold text-xl tracking-tight text-slate-900">{schoolName}</span>
                    </div>

                    <div className="hidden md:flex items-center gap-8 font-medium text-sm text-slate-600">
                        <a href="#" className="hover:text-orange-600 transition-colors">Programs</a>
                        <a href="#" className="hover:text-orange-600 transition-colors">Our Studios</a>
                        <a href="#" className="hover:text-orange-600 transition-colors">Faculty</a>
                        <a href="#" className="hover:text-orange-600 transition-colors">Student Life</a>
                    </div>

                    <div className="flex gap-4">
                        <Button variant="ghost" className="hidden md:flex text-slate-900 hover:bg-slate-100 font-semibold">
                            Request Info
                        </Button>
                        <Button className="bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-md px-6 shadow-md transition-all hover:translate-y-[-1px]">
                            Apply Now
                        </Button>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <header className="relative bg-slate-900 py-24 px-6 overflow-hidden">
                <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1590602847861-f357a9332bbc?q=80&w=1920&auto=format&fit=crop')] bg-cover bg-center opacity-20"></div>
                <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/90 to-transparent"></div>
                
                <div className="max-w-7xl mx-auto relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400 text-xs font-bold uppercase tracking-wider mb-6">
                            <Radio size={12} /> The Voice of the Future
                        </div>
                        <h1 className="text-5xl md:text-6xl font-black text-white leading-tight mb-6">
                            Master the Art of <br/>
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-500">Broadcasting.</span>
                        </h1>
                        <p className="text-lg text-slate-300 mb-8 max-w-lg leading-relaxed">
                            From radio journalism to modern podcasting, gain the technical skills and vocal confidence to be heard by the world.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4">
                            <Button className="h-12 px-8 bg-orange-600 hover:bg-orange-700 text-white font-bold text-base rounded-md">
                                Explore Courses
                            </Button>
                            <Button variant="outline" className="h-12 px-8 border-white/20 text-white hover:bg-white/10 font-bold text-base rounded-md">
                                Virtual Campus Tour
                            </Button>
                        </div>
                        
                        <div className="mt-12 flex items-center gap-8 text-slate-400 text-sm font-medium">
                            <div className="flex items-center gap-2">
                                <Users size={18} className="text-orange-500" />
                                <span>500+ Alumni Network</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Star size={18} className="text-orange-500" />
                                <span>Accredited Program</span>
                            </div>
                        </div>
                    </div>
                    
                    {/* Hero Card/Feature */}
                    <div className="hidden lg:block relative">
                         <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-8 transform rotate-3 hover:rotate-0 transition-transform duration-500">
                             <div className="flex items-center gap-4 mb-6">
                                 <div className="w-12 h-12 bg-orange-600 rounded-full flex items-center justify-center text-white">
                                     <PlayCircle size={24} fill="currentColor" />
                                 </div>
                                 <div>
                                     <h3 className="text-white font-bold text-lg">Student Spotlight</h3>
                                     <p className="text-slate-400 text-sm">Listen to award-winning projects</p>
                                 </div>
                             </div>
                             <div className="space-y-4">
                                 {[1, 2, 3].map((i) => (
                                     <div key={i} className="bg-white/10 rounded-lg p-3 flex items-center gap-3">
                                         <div className="w-8 h-8 bg-slate-800 rounded flex items-center justify-center text-slate-500 text-xs font-bold">
                                             {i}
                                         </div>
                                         <div className="flex-1">
                                             <div className="h-2 w-24 bg-slate-700 rounded mb-1"></div>
                                             <div className="h-1.5 w-16 bg-slate-700/50 rounded"></div>
                                         </div>
                                         <div className="text-slate-500 text-xs">3:45</div>
                                     </div>
                                 ))}
                             </div>
                         </div>
                    </div>
                </div>
            </header>

            {/* Programs / Stats */}
            <section className="py-20 px-6 max-w-7xl mx-auto">
                <div className="text-center mb-16">
                    <h2 className="text-3xl font-bold text-slate-900 mb-4">Professional Certification Tracks</h2>
                    <p className="text-slate-600 max-w-2xl mx-auto">
                        Our curriculum is designed by industry veterans from NPR, BBC, and top podcast networks.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {[
                        { icon: Mic, title: "Voice & Performance", desc: "Techniques for respiration, phonation, and articulation." },
                        { icon: Headphones, title: "Audio Engineering", desc: "Master ProTools, mixing, and sound design." },
                        { icon: Podcast, title: "Production & Business", desc: "From content strategy to monetization models." }
                    ].map((item, i) => (
                        <div key={i} className="group bg-white p-8 rounded-xl shadow-sm border border-slate-200 hover:shadow-xl hover:border-orange-200 transition-all">
                            <div className="w-14 h-14 bg-orange-50 text-orange-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                                <item.icon size={28} />
                            </div>
                            <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                            <p className="text-slate-500 leading-relaxed mb-6">{item.desc}</p>
                            <a href="#" className="inline-flex items-center text-orange-600 font-bold text-sm hover:underline">
                                Course Details <ArrowRight size={16} className="ml-1" />
                            </a>
                        </div>
                    ))}
                </div>
            </section>

             {/* Footer */}
            <footer className="bg-slate-900 text-slate-300 py-16 px-6">
                <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-12">
                    <div className="col-span-1 md:col-span-1">
                        <div className="flex items-center gap-2 mb-6 text-white">
                            <Mic size={24} className="text-orange-500" />
                            <span className="font-bold text-lg">{schoolName}</span>
                        </div>
                        <p className="text-sm text-slate-500 leading-relaxed">
                            Educating the next generation of media professionals since 1998.
                        </p>
                    </div>
                    
                    <div>
                        <h4 className="text-white font-bold mb-4">Programs</h4>
                        <ul className="space-y-2 text-sm">
                            <li><a href="#" className="hover:text-orange-500 transition-colors">Certificate in Podcasting</a></li>
                            <li><a href="#" className="hover:text-orange-500 transition-colors">Radio Journalism</a></li>
                            <li><a href="#" className="hover:text-orange-500 transition-colors">Sound Engineering</a></li>
                        </ul>
                    </div>

                    <div>
                        <h4 className="text-white font-bold mb-4">Admissions</h4>
                        <ul className="space-y-2 text-sm">
                            <li><a href="#" className="hover:text-orange-500 transition-colors">How to Apply</a></li>
                            <li><a href="#" className="hover:text-orange-500 transition-colors">Tuition & Fees</a></li>
                            <li><a href="#" className="hover:text-orange-500 transition-colors">Scholarships</a></li>
                        </ul>
                    </div>

                    <div>
                        <h4 className="text-white font-bold mb-4">Contact</h4>
                        <ul className="space-y-2 text-sm">
                            <li>admissions@broadcast.edu</li>
                            <li>+1 (800) 555-0199</li>
                            <li>123 Media Ave, NY</li>
                        </ul>
                    </div>
                </div>
                <div className="max-w-7xl mx-auto mt-12 pt-8 border-t border-slate-800 text-center text-xs text-slate-600">
                    &copy; 2026 {schoolName}. All Rights Reserved.
                </div>
            </footer>
        </div>
    );
};

export default PodcastMedia;
